import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { SIGN } from "@/constants/testIds";

/**
 * Signature pad — 3 modes (Type / Draw / Upload).
 * Fixed: canvas now scales to its container with device-pixel-ratio for crisp 1:1 drawing.
 * Parent can pass `resetKey` to force a clean slate when the stage changes.
 */
export default function SignaturePad({ value, onChange, label = "Signature", disabled = false }) {
  const [mode, setMode] = useState(value?.mode || "type");
  const [typed, setTyped] = useState(value?.mode === "type" ? value.value : "");
  const [uploaded, setUploaded] = useState(value?.mode === "upload" ? value.value : "");
  const [drawn, setDrawn] = useState(value?.mode === "draw" ? value.value : "");

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const dprRef = useRef(1);

  // Propagate changes upstream
  useEffect(() => {
    if (mode === "type") onChange?.({ mode: "type", value: typed });
    if (mode === "upload") onChange?.({ mode: "upload", value: uploaded });
    if (mode === "draw") onChange?.({ mode: "draw", value: drawn });
    // eslint-disable-next-line
  }, [mode, typed, uploaded, drawn]);

  // Size the canvas to its container with device-pixel-ratio so it isn't blurry / zoomed.
  useLayoutEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const setupCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      dprRef.current = dpr;
      const cssWidth = container.clientWidth;
      const cssHeight = 150;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0A0A0A";
      ctx.lineWidth = 2;
    };
    setupCanvas();
    const ro = new ResizeObserver(setupCanvas);
    ro.observe(container);
    return () => ro.disconnect();
  }, [mode]);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    if (disabled) return;
    e.preventDefault?.();
    drawingRef.current = true;
    lastRef.current = getXY(e);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault?.();
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setDrawn(canvasRef.current.toDataURL("image/png"));
  };
  const clearDraw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setDrawn("");
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUploaded(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="border border-zinc-300 rounded-sm bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-600">{label}</div>
        <div className="flex gap-1">
          {["type", "draw", "upload"].map((m) => (
            <button
              key={m}
              type="button"
              disabled={disabled}
              data-testid={m === "type" ? SIGN.tabType : m === "draw" ? SIGN.tabDraw : SIGN.tabUpload}
              onClick={() => setMode(m)}
              className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                mode === m
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
              }`}
            >
              {m === "type" ? "Type" : m === "draw" ? "Draw" : "Upload"}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3">
        {mode === "type" && (
          <input
            type="text"
            disabled={disabled}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type your full name to sign"
            className="w-full border border-zinc-300 rounded-sm px-3 py-2 font-serif italic text-2xl text-zinc-900"
            data-testid="sign-type-input"
          />
        )}

        {mode === "draw" && (
          <div ref={containerRef} className="w-full">
            <canvas
              ref={canvasRef}
              onMouseDown={startDraw}
              onMouseMove={moveDraw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={moveDraw}
              onTouchEnd={endDraw}
              style={{ touchAction: "none", display: "block" }}
              className="w-full bg-zinc-50 border border-dashed border-zinc-400 rounded-sm cursor-crosshair"
              data-testid="sign-draw-canvas"
            />
            <button
              type="button"
              onClick={clearDraw}
              disabled={disabled}
              data-testid={SIGN.clearBtn}
              className="mt-2 text-xs text-zinc-600 hover:text-zinc-900 underline"
            >
              Clear
            </button>
          </div>
        )}

        {mode === "upload" && (
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={onUpload}
              disabled={disabled}
              className="text-sm"
              data-testid="sign-upload-input"
            />
            {uploaded && (
              <img
                src={uploaded}
                alt="signature"
                className="mt-2 max-h-24 border border-zinc-200 rounded-sm bg-white"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
